import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'
import {emitBatchActivity} from '../components/platform/payroll/components/BatchActivity/batchActivityBus'
import type {GroupByField, SortByField} from './billingPreferencesApi'
import type {PaymentTermsValue} from './paymentTerms.constants'

/**
 * Batch Status Enum (from backend)
 *
 * UPDATED REQUIREMENT (Jan 2025): Status flow clarified
 * NEW FLOW (per Joseph's requirements):
 * - Draft → InProgress → Finalized (batch creation/editing)
 * - After invoice generation: Open (pending customer approval)
 * - After customer approval: Approved (ready for payroll processing)
 * - After Process Payroll clicked: Closed (sent to payroll software)
 *
 * OLD FLOW: Draft → InProgress → Finalized → Approved
 * The enum values remain the same, but interpretation changes:
 * - "Finalized" = batch ready, invoice auto-generated (customer sees as "Open")
 * - "Approved" = customer approved invoice (Platform Admin sees as "Approved")
 * - "Archived/Closed" = sent to payroll software (Platform Admin sees as "Closed")
 */
export enum BatchStatus {
  Draft = 'Draft',
  InProgress = 'InProgress',
  Finalized = 'Finalized',
  Approved = 'Approved',
  Invoiced = 'Invoiced', // ADDED: After invoice is generated from batch
  PaystubsApproved = 'PaystubsApproved', // ADDED: After all paystubs are approved
  PayrollSent = 'PayrollSent', // ADDED: After sent to payroll microservice
  Completed = 'Completed', // ADDED: Payroll fully processed
  Voided = 'Voided', // ADDED: Batch was voided/cancelled
  // Legacy/UI statuses (may be removed in future):
  Rejected = 'Rejected',
  Archived = 'Archived',
  Closed = 'Closed',
}

/**
 * Invoice Status Enum (from backend)
 */
export enum InvoiceStatus {
  Pending = 'Pending',
  Draft = 'Draft',
  Sent = 'Sent',
  Approved = 'Approved',
  Paid = 'Paid',
  Overdue = 'Overdue',
  Cancelled = 'Cancelled',
  Voided = 'Voided',
}

export enum RevisionType {
  CorrectionOnly = 'CorrectionOnly',
  FullRestatement = 'FullRestatement',
}

export enum CreditAdjustmentStatus {
  Active = 'Active',
  Reversed = 'Reversed',
}

/**
 * EOR Item for EOR List Response
 *
 * STFFLG-3596 / SHR-3848: Reconciled to the real backend EORResponse shape.
 * The previous interface (employeeCount / activeCompanies / lastPayrollDate) was
 * stale — GET /payroll/eors returns id, name, location, status, siteCount,
 * eorInternalCode, federalEin. Open-batch indicator counts were added by BE.
 */
export interface EORItem {
  id: string
  name: string
  location: string
  status: string
  siteCount: number
  eorInternalCode?: string
  federalEin?: string
  /**
   * STFFLG-3596: number of batches for this EOR in a "Pending Approval" state
   * (Draft / InProgress / Finalized). Absent/undefined when BE omits it.
   */
  pendingApprovalCount?: number
  /**
   * STFFLG-3596: number of batches for this EOR in a "Ready to Process" state
   * (Approved / Invoiced / PaystubsApproved). Absent/undefined when BE omits it.
   */
  readyToProcessCount?: number
}

/**
 * EOR List Response
 */
export interface EORListResponse {
  eors: EORItem[]
}

/**
 * Client Item for Client List Response
 */
export interface ClientItem {
  id: string
  name: string
  location: string
  // STFFLG-3643/3596: synced to the backend ClientResponse — it returns
  // lastPayrollDate/lastImportDate/lastActivity + the batch counts, NOT the
  // previously-declared (never-populated) employeeCount/sitesCount.
  lastPayrollDate?: string | null
  lastImportDate?: string | null
  status: string
  lastActivity: string
  /**
   * STFFLG-3596: number of batches for this client in a "Pending Approval" state
   * (Draft / InProgress / Finalized). Absent/undefined when BE omits it.
   */
  pendingApprovalCount?: number
  /**
   * STFFLG-3596: number of batches for this client in a "Ready to Process" state
   * (Approved / Invoiced / PaystubsApproved). Absent/undefined when BE omits it.
   */
  readyToProcessCount?: number
}

/**
 * Client List Response
 */
export interface ClientListResponse {
  clients: ClientItem[]
}

/**
 * Create Batch Request
 * UPDATED: Field names match mspplatform backend
 */
export type BatchType = 'Regular' | 'Custom'

export interface CreateBatchRequest {
  agencyId: string
  companyId: string
  paymentPeriodStart: Date | string
  paymentPeriodEnd: Date | string
  payDate: Date | string
  batchName: string
  status?: BatchStatus
  description?: string
  batchType?: BatchType
  /** STFFLG-4278: approval bills the client but creates no paystubs. The BE rejects it unless the company allows it. */
  isInvoiceOnly?: boolean
}

/**
 * Update Batch Request
 * UPDATED: Field names match mspplatform backend
 */
export interface UpdateBatchRequest {
  id: string
  agencyId: string
  companyId: string
  batchName?: string
  paymentPeriodStart?: Date | string
  paymentPeriodEnd?: Date | string
  payDate?: Date | string
  status?: BatchStatus
  description?: string
}

/**
 * Batch Response
 * UPDATED: Matches mspplatform backend structure
 * - Removed: batchId (use id instead), totalGross, totalNet, totalDeductions, totalTaxes
 * - Renamed: payPeriodStartDate → paymentPeriodStart, payPeriodEndDate → paymentPeriodEnd, notes → description
 * - Added: batchNumber, externalBatchId, statusChangedAt, statusChangedBy
 * - STFFLG-4179: the list figures totalBill, totalInvoice, totalInvoiceBeforeAdjustments and
 *   employeeCount (back, now a distinct-employee count), with hasLiveInvoice and the per-figure
 *   error flags. GET /payroll/batches fills them only with `includeTotals` (see getBatches), so
 *   every other read leaves them unset. Data rules: docs/deep-dive/batch-table-columns.md §4.
 */
export interface BatchResponse {
  id: string
  batchName: string
  batchNumber: string
  agencyId: string
  companyId: string
  paymentPeriodStart: Date
  paymentPeriodEnd: Date
  payDate: Date
  status: BatchStatus
  batchType?: BatchType
  statusChangedAt?: Date
  statusChangedBy?: string
  /**
   * Legacy field still returned by the API but absent from the modern shape.
   * The batch list reads it (filter + Processed Date sort) and falls back to
   * statusChangedAt when it is missing. Declared so those reads stay typed
   * rather than casting through `any` at each call site.
   */
  processedDate?: Date | string
  description?: string
  externalBatchId?: string
  createdDate: Date
  modifiedDate: Date
  createdBy: string
  isArchived?: boolean
  archivedAt?: Date
  archivedBy?: string
  originalBatchId?: string
  revisionNumber?: number
  revisionType?: RevisionType
  isRevision?: boolean
  revisions?: BatchResponse[]
  voidedDate?: Date
  voidedBy?: string
  voidReason?: string
  /**
   * The approver's email (the latest invoice approval). From getBatches always a string or unset:
   * it normalizes the batch's own ActorStamp object, which the server can send here (STFFLG-4179 D3).
   */
  approvedBy?: string
  approvedDate?: Date
  totalPaid?: number
  /**
   * The invoice number of every invoice on this batch, e.g. ['NIMI-0005']. One per invoice: a batch
   * split by Max Invoice Amount or by department carries several (STFFLG-3883).
   */
  invoiceNumbers?: string[]
  paymentIssueCount?: number
  paymentIssueSummary?: string
  /** Number of live credit adjustments on the invoice linked to this batch. BE omits the field (or sends 0) when none exist. */
  creditAdjustmentCount?: number
  agencyName?: string
  companyName?: string
  /** Non-disbursing overpayment-recovery correction batch. */
  isCorrection?: boolean
  /** STFFLG-4278: invoice-only batch — its approval creates the client invoice but no paystubs or payments. */
  isInvoiceOnly?: boolean
  /** For correction batches: the original (already-paid) batch being corrected. */
  correctionOfBatchId?: string
  /** Employees in this batch with a declining-balance deduction carrying a balance forward. */
  carryForwardCount?: number
  /** Human-readable summary of the carried-forward balances (for the batch-table warning tooltip). */
  carryForwardSummary?: string
  /*
   * STFFLG-4179: the Batches table's figures, filled only by the list with `includeTotals` (every
   * other read leaves them unset, and so does a backend from before the change). Each row is its
   * own batch: a nested revision carries its own, never rolled into its parent. "Live" invoice =
   * neither Voided nor Cancelled. Rounded to cents on the server. The cells read them through
   * `batchFigure` (batchTableColumns.tsx, tracker §4.3), never through formatCurrency, which turns a
   * missing value into $0.00.
   */
  /**
   * Σ total over the batch's live invoices (D11). Unset with no live invoice, on a Voided batch, or
   * when the invoice read failed (`invoiceTotalsError`).
   */
  totalBill?: number
  /**
   * Σ over the live invoices of (total − their live credit adjustments) (D1); a debit can make it
   * higher than totalBill. Unset when totalBill is, or when the credit read failed
   * (`creditTotalsError`).
   */
  totalInvoice?: number
  /** Σ total over the same live invoices: Total Invoice's "before credit adjustments" figure. */
  totalInvoiceBeforeAdjustments?: number
  /**
   * Distinct employees across the batch's invoice items (D2): 0 for a live invoice with no items,
   * unset with neither items nor a live invoice, or on a Voided batch.
   */
  employeeCount?: number
  /** At least one live invoice. Unset when the invoice read failed. */
  hasLiveInvoice?: boolean
  /** The invoice read failed: Total Paid, Total Bill and Total Invoice could not be computed. */
  invoiceTotalsError?: boolean
  /** The credit-adjustment read failed: Total Invoice could not be computed (live-invoice rows only). */
  creditTotalsError?: boolean
  /** The employee-count read failed: Employees could not be computed. */
  employeeCountError?: boolean
}

/**
 * Starts an overpayment-recovery correction: mirrors the selected already-paid
 * paystubs into a non-disbursing Custom batch the admin edits down and approves.
 * On approval the originals are reversed and an Overpayment Recovery deduction
 * is attached.
 */
export interface StartCorrectionBatchRequest {
  originalBatchId: string
  paystubIds: string[]
  paymentPeriodStart?: Date | string
  paymentPeriodEnd?: Date | string
  payDate?: Date | string
  batchName?: string
}

/**
 * Send to Payroll Response
 * Returned from POST /payroll/batches/:batchId/send-to-payroll
 */
export interface SendToPayrollResponse {
  success: boolean
  message: string
  data: {
    batchId: string
    externalBatchId: string
    status: string
    paystubsProcessed: number
    invoiceItemsCreated: number
    sentAt: string
  }
}

/**
 * Create Batch Earnings Request (for employee earnings records)
 */
export interface CreateBatchEarningsRequest {
  batchId: string
  timecards: TimecardData[]
}

/**
 * Timecard Data for earnings calculation
 */
export interface TimecardData {
  employeeId: string
  regularHours: number
  overtimeHours: number
  doubleTimeHours: number
  payRate: number
  overtimeRate?: number
  doubleTimeRate?: number
}

/**
 * Daily Breakdown for timecard (from external app)
 */
export interface DailyTimecardBreakdown {
  date: string
  dayOfWeek: string
  regularHours: number
  /**
   * STFFLG-3157 — per-day salary hours.
   * The msp wire field is `salary` (see msp-service payroll/timecard/schemas/timecard-data.schema.ts).
   * SHR names the same daily concept `salaryHours`; do NOT copy that name here or it reads
   * `undefined` forever. Note the vendor (Ronos) currently sends 0 on every daily row and
   * reclassifies a salaried week's hours to the WEEKLY `salary` field instead — so this is
   * expected to be 0 in production today. That is the data, not a bug.
   */
  salary?: number
  /** STFFLG-3157 — per-day retro salary hours. Not surfaced yet (SHR's retro row has no salary either). */
  retroSalary?: number
  overtimeHours: number
  doubleTimeHours: number
  totalHours: number
  vacationHours?: number
  sickHours?: number
  bereavementHours?: number
  unpaidHours?: number
  holidayHours?: number
  mealPenalty?: number
  punches?: Array<{
    punchIn: string
    punchOut: string
  }>
}

/**
 * Timecard Response from GET /payroll/timecards (from external app)
 */
export interface TimecardResponse {
  id: string
  versionKey: number
  createdDate: string
  modifiedDate: string
  employeeId: string
  applicantId: string
  assignmentId: string
  weekStartDate: string
  weekEndDate: string
  regularTime: number
  /**
   * STFFLG-3157 — weekly salary hours for salaried employees. This is where a salaried week's
   * hours actually live: the vendor reports `regularTime: 0` with `salary` carrying the hours.
   */
  salary?: number
  /** STFFLG-3157 — weekly retro salary hours. Typed for completeness; not rendered (SHR parity). */
  retroSalary?: number
  overtime: number
  doubleTime: number
  vacationHours: number
  sickHours: number
  holidayHours: number
  unpaidHours: number
  bereavementHours: number
  mealPenalty?: number
  retroRegularTime?: number
  retroOvertime?: number
  retroDoubleTime?: number
  retroMealPenalty?: number
  retroVacationHours?: number
  retroSickHours?: number
  retroHolidayHours?: number
  retroUnpaidHours?: number
  retroBereavementHours?: number
  dailyBreakdown: DailyTimecardBreakdown[]
  processed: boolean
  receivedDate: string
}

/**
 * Sort fields for paginated timecards
 */
export type TimecardSortField = 'employeeName' | 'totalHours'

/**
 * Parameters for paginated timecards request
 */
export interface TimecardPagingParams {
  pageNumber: number
  pageSize: number
  companyId?: string
  siteId?: string
  departmentId?: string
  jobTitle?: string
  employeeName?: string
  weekStartDate?: string // Format: YYYY-MM-DD
  weekEndDate?: string // Format: YYYY-MM-DD
  sortBy?: TimecardSortField
  sortOrder?: 'asc' | 'desc'
}

/**
 * Individual timecard item in paginated response
 */
export interface TimecardPaginatedItem {
  id: string
  versionKey: number
  createdDate: string
  modifiedDate: string
  employeeId: string
  applicantId: string
  assignmentId: string
  weekStartDate: string
  weekEndDate: string
  regularTime: number
  /** STFFLG-3157 — weekly salary hours (see TimecardResponse.salary). */
  salary?: number
  /** STFFLG-3157 — weekly retro salary hours. Typed for completeness; not rendered (SHR parity). */
  retroSalary?: number
  overtime: number
  doubleTime: number
  vacationHours: number
  sickHours: number
  holidayHours: number
  unpaidHours: number
  bereavementHours: number
  mealPenalty?: number
  retroRegularTime?: number
  retroOvertime?: number
  retroDoubleTime?: number
  retroVacationHours?: number
  retroSickHours?: number
  retroHolidayHours?: number
  retroUnpaidHours?: number
  retroBereavementHours?: number
  retroMealPenalty?: number
  totalHours: number
  processed: boolean
  receivedDate: string
  // Looked-up fields from related collections
  employeeName: string
  firstName: string
  lastName: string
  siteName: string
  siteId: string
  departmentName: string
  departmentId: string
  jobTitle: string
  dailyBreakdown: DailyTimecardBreakdown[]
}

/**
 * Paginated timecards response
 */
export interface TimecardPagingResponse {
  timecards: TimecardPaginatedItem[]
  totalRecords: number
  pageNumber: number
  pageSize: number
  totalPages: number
}

/**
 * Earnings Record Response
 */
export interface EarningsRecordResponse {
  id: string
  batchId: string
  employeeId: string
  employeeName: string
  regularHours: number
  overtimeHours: number
  doubleTimeHours: number
  regularPay: number
  overtimePay: number
  doubleTimePay: number
  grossPay: number
  federalTax: number
  stateTax: number
  socialSecurityTax: number
  medicareTax: number
  totalTaxes: number
  deductions: number
  netPay: number
  createdDate: string
}

/**
 * Create Invoice Request (Legacy - kept for compatibility)
 */
export interface CreateInvoiceRequest {
  batchId: string
  dueDate: string
  notes?: string
}

/**
 * Create Blank Invoice Request
 * UPDATED: New endpoint for manual invoice creation
 */
export interface CreateBlankInvoiceRequest {
  companyId: string
  payPeriodStart: Date | string
  payPeriodEnd: Date | string
  agencyId: string
  batchId?: string
}

/**
 * Add Invoice Item Request
 * UPDATED: For adding employees to invoices manually
 */
export interface AddInvoiceItemRequest {
  applicantId: string
  assignmentId: string
  earningsLines: EarningsLine[]
  /**
   * Back pay only — YYYY-MM-DD Monday/Sunday of the prior work week, sent with
   * isPriorPeriod:true. Omit all three for a current-period item: the backend
   * then books the item against the invoice period (MSP periods are arbitrary
   * spans, never chunked by the UI).
   */
  weekStartDate?: string
  weekEndDate?: string
  isPriorPeriod?: boolean
}

/**
 * Update Invoice Item Request
 * UPDATED: For updating employee earnings in invoice
 */
export interface UpdateInvoiceItemRequest {
  earningsLines: EarningsLine[]
}

/**
 * Invoice Generation Summary
 * UPDATED: Summary of auto-generated invoice from batch
 */
export interface InvoiceGenerationSummary {
  totalEmployees: number
  totalTimecards: number
  errors: InvoiceGenerationError[]
}

/**
 * One entry in the generation summary's errors array. Today the backend emits
 * two shapes:
 *  - per-applicant build failures (no `type`, has `applicantId`/`error`)
 *  - overspent PO warnings (`type: 'po-overspend'`)
 * Future shapes should be added to the union with a discriminator on `type`.
 */
export interface PoOverspendError {
  type: 'po-overspend'
  purchaseOrderId: string
  poNumber: string
  poAmount: number
  amountUsed: number
  remainingAmount: number
}

export interface ApplicantGenerationError {
  applicantId: string
  error: string
}

export type InvoiceGenerationError =
  | PoOverspendError
  | ApplicantGenerationError
  | Record<string, unknown>

/**
 * Generate Invoice Response
 * UPDATED: Response from generateInvoiceFromBatch endpoint
 */
export interface GenerateInvoiceResponse {
  invoice: InvoiceResponse
  items: InvoiceItem[]
  summary: InvoiceGenerationSummary
}

/**
 * Earnings Line - represents one paycode type (regular, overtime, etc.) for an employee
 * UPDATED: Matches mspplatform backend InvoiceItem.earningsLines structure
 */
export interface EarningsLine {
  lineId?: string // Stable per-line identifier (UUID). Absent on legacy lines; the server assigns one.
  systemCode: string // "regularTime", "overtime", "doubleTime", etc.
  paycodeName: string // "REGULAR", "OVERTIME", "DT"
  paycodeId: string
  payUnit: number // Hours worked
  billUnit: number // Hours billed (same except unpaidHours = 0)
  payRate: number // Base rate or multiplied for OT/DT
  billRate: number // payRate with markup applied
  payAmount: number // payUnit * payRate
  billAmount: number // billUnit * billRate * billablePercentage
  markupPercentage: number
  billablePercentage: number
  nonBillablePercentage: number
  isTip?: boolean
  customerCredit?: number // Money the customer already paid the employee directly (e.g. cash tips); reduces the customer bill only
  /** True when this line's paycode is a reimbursement (money paid to the employee, split out of earnings on the paystub). */
  isReimbursement?: boolean
  /** For reimbursements: true = pre-tax/taxable (added to gross + taxed); false = post-tax/non-taxable. */
  isPreTax?: boolean
}

/**
 * Request DTO for updating an individual earnings line within an invoice item
 * All fields are optional - only provided fields will be updated
 * ADDED: New DTO for PUT /invoices/:invoiceId/items/:itemId/earnings/:paycodeId
 */
export interface UpdateEarningsLineRequest {
  lineId?: string // Target line id; disambiguates when multiple lines share the same paycodeId
  paycodeId?: string // New paycode ID (for changing paycode type)
  paycodeName?: string // New paycode name
  systemCode?: string // New system code
  payUnit?: number // Hours worked
  billUnit?: number // Hours billed
  payRate?: number // Pay rate per hour
  billRate?: number // Bill rate per hour
  markupPercentage?: number
  billablePercentage?: number // 0-100
  nonBillablePercentage?: number // 0-100
  isTip?: boolean
  customerCredit?: number // Reduces the customer bill only; never affects pay/tax/net pay
}

/**
 * Invoice Item - represents ONE EMPLOYEE on an invoice
 * UPDATED: Matches mspplatform backend structure with full details
 * Groups all timecards and earnings for that employee for the pay period
 */
export interface InvoiceItem {
  id: string
  versionKey?: number
  batchId: string
  invoiceId: string
  applicantId: string
  assignmentId: string
  employeeName: string // "lastName, firstName" format
  firstName: string
  lastName: string
  ssn: string // Last 4 digits
  billToName: string // Customer/company name
  departmentName: string
  /** Department ID from the assignment — the key Separate Invoice By partitions on. Absent on legacy items. */
  departmentId?: string
  /** Site ID from the assignment. Site separation keys on it; department keys include it. */
  siteId?: string
  branch: string // Agency/branch name
  weekWorked: string // "MM/DD/YY - MM/DD/YY"
  weekStartDate?: string // ISO date string
  weekEndDate?: string // ISO date string
  payPeriod: string // "Weekly", "Bi-weekly", etc.
  isPriorPeriod?: boolean // True when this item covers a prior work week (back pay)
  itemPay: number // Total pay for employee
  itemBill: number // Total bill for employee
  itemCredit?: number // Total customer credit for employee (sum of earnings customerCredit)
  orderType: string // "TEMP", etc.
  poNumber: string
  markupName: string
  markupPct: number // Calculated: itemBill / itemPay
  earningsLines: EarningsLine[] // CRITICAL: Detailed breakdown by paycode
  timecardIds: string[]
  createdDate?: Date
  modifiedDate?: Date
}

/**
 * Invoice Response
 * UPDATED: Matches mspplatform backend structure
 */
export interface InvoiceResponse {
  id: string
  versionKey?: number
  invoiceNumber: string
  batchId: string
  companyId: string
  customerName: string
  agencyId: string
  agencyName: string
  payPeriodStart: Date
  payPeriodEnd: Date
  invoiceDate: Date
  dueDate?: Date
  status: InvoiceStatus
  sentDate?: Date
  sentBy?: string
  approvedDate?: Date
  approvedBy?: string
  totalPay: number
  totalBill: number
  totalCredit?: number // Total customer credit; subtracted from totalBill to compute total
  subtotal: number
  taxes: number
  total: number
  pdfUrl?: string
  voidedDate?: Date
  voidedBy?: string
  voidReason?: string
  createdDate?: Date
  modifiedDate?: Date
  createdBy?: string
  revisionCode?: string
  originalInvoiceId?: string
  revisionNumber?: number
  revisionType?: RevisionType
  /*
   * STFFLG-3883 — Max Invoice Amount split.
   *
   * Distinct from the revision fields above and never interchangeable with them: splits are
   * SIBLINGS produced by one billing run, revisions are SUCCESSORS of one invoice. A revision that
   * is itself split carries both sets.
   *
   * All absent when the run produced a single invoice, i.e. for every customer without a cap.
   */
  /** Groups the invoices produced by one capped billing run. */
  splitGroupId?: string
  /** 1-based position within that group — the "2" in "2 of 3". */
  splitIndex?: number
  /** How many invoices the run produced. 1 or absent means it was not split. */
  splitCount?: number
  /** The cap that produced this split, kept for audit. */
  splitCapAmount?: number
  /** Department ID this invoice bills — present only on department-separated runs. */
  splitDepartmentId?: string
  /** Department name this invoice bills — what switcher labels and PDFs show. */
  splitDepartmentName?: string
  /** Site ID this invoice bills (site-separated runs), or the owning site of the department billed. */
  splitSiteId?: string
  /** Site name this invoice bills — present only on site-separated runs. */
  splitSiteName?: string
  /**
   * Client delivery status — set when an invoice is Approved.
   * 'Sent': client was emailed at approval time.
   * 'Skipped': invoice was approved without emailing the client; can be sent later via sendInvoiceToClient.
   */
  clientDeliveryStatus?: 'Sent' | 'Skipped'
  /**
   * Audit log of every send-to-client action performed on this invoice.
   * Each entry is appended by POST /payroll/invoices/:invoiceId/send-to-client.
   */
  clientDeliveries?: {sentAt: string; sentBy: string; recipients: string[]}[]
  /**
   * The customer's billing preferences AS THEY WERE when this invoice was generated.
   *
   * Frozen deliberately — see the backend's `BillingPreferencesSnapshot` docblock
   * (invoice.schema.ts). The FE cares about one field of it: `maxInvoiceAmount`, which is the cap
   * that actually produced this split. Reading the company's LIVE cap instead would show the user a
   * number that had nothing to do with the invoice in front of them.
   */
  billingPreferencesSnapshot?: BillingPreferencesSnapshot
}

/** One invoice of a batch, as it is now or as a re-split would leave it. */
export interface BatchSplitInvoiceView {
  /** Absent for an invoice the re-split would mint. */
  invoiceId?: string
  invoiceNumber?: string
  /** True for an invoice the re-split would mint (it has no number yet). */
  isNew?: boolean
  status?: string
  /** The department or site the invoice bills, on a separated batch. */
  segmentName?: string
  total: number
  employeeCount: number
}

/** One employee-week a re-split would move to another invoice. */
export interface BatchSplitMove {
  itemId: string
  employeeName: string
  weekWorked?: string
  fromInvoiceNumber: string
  /** An invoice number, or "New invoice N" for one the re-split would mint. */
  toInvoiceNumber: string
}

/**
 * GET /payroll/batches/:batchId/split-status — mirrors mspplatform's BatchSplitStatus.
 *
 * Manual edits never move anyone; when an edit leaves the batch needing a re-split (an invoice over
 * its snapshot Max Invoice Amount holding more than one employee-week, or employees from another
 * department/site), `needed` is true, `reasons` says why, and `proposed`/`moves` preview exactly
 * what POST resplit will do. Approve and Send are refused by the backend until it is done.
 */
export interface BatchSplitStatus {
  applicable: boolean
  canResplit: boolean
  needed: boolean
  reasons: string[]
  maxInvoiceAmount: number | null
  separateInvoiceBy: string | null
  current: BatchSplitInvoiceView[]
  proposed: BatchSplitInvoiceView[]
  moves: BatchSplitMove[]
  retired: string[]
}

/**
 * Billing preferences frozen onto an invoice at generation time.
 * Mirrors mspplatform's `BillingPreferencesSnapshot` (invoice.schema.ts) field for field.
 */
export interface BillingPreferencesSnapshot {
  groupInvoiceBy: GroupByField[]
  sortInvoiceBy: SortByField[]
  invoiceStyleId?: string
  deliveryEmails: string[]
  paymentTerms?: PaymentTermsValue
  invoiceDay?: string
  /** STFFLG-3883 — the cap in force at generation. Absent or null = the run was uncapped. */
  maxInvoiceAmount?: number | null
}

/**
 * Invoice with Items Response
 */
export interface InvoiceWithItemsResponse extends InvoiceResponse {
  items: InvoiceItem[]
  creditAdjustments?: InvoiceCreditAdjustment[]
}

// ============================================================================
// CREDIT ADJUSTMENT TYPES
// ============================================================================

export interface InvoiceCreditAdjustment {
  id: string
  adjustmentNumber: string
  invoiceId: string
  batchId: string
  agencyId: string
  companyId: string
  applicantId?: string
  employeeName?: string
  amount: number
  reference?: string
  reason?: string
  status: CreditAdjustmentStatus
  isReversal: boolean
  reversedAdjustmentId?: string
  reversedBy?: string
  reversedAt?: Date
  createdBy: string
  createdDate: Date
}

export interface ApplyCreditRequest {
  amount: number
  applicantId?: string
  employeeName?: string
  reference?: string
  reason?: string
}

// ============================================================================
// BATCH REVISION TYPES
// ============================================================================

export interface RevisionItemEarningsLine {
  lineId?: string // Stable per-line identifier (UUID); preserves line identity across revisions
  systemCode: string
  paycodeName: string
  paycodeId: string
  payUnit?: number
  billUnit?: number
  payRate?: number
  billRate?: number
  payAmount: number
  billAmount: number
  markupPercentage: number
  billablePercentage: number
  nonBillablePercentage: number
  isTip?: boolean
}

export interface RevisionItem {
  originalItemId?: string
  applicantId: string
  assignmentId: string
  employeeName: string
  firstName: string
  lastName: string
  ssn: string
  earningsLines: RevisionItemEarningsLine[]
}

export interface CreateRevisionRequest {
  revisionType: RevisionType
  items: RevisionItem[]
}

export interface CreateRevisionResponse {
  batch: BatchResponse
  invoice: InvoiceResponse
  itemsCreated: number
}

/**
 * Toast Import Request (for timecard imports from POS systems)
 */
export interface ToastImportRequest {
  batchId: string
  restaurantId: string
  startDate: string
  endDate: string
}

/**
 * Toast Import Response
 */
export interface ToastImportResponse {
  success: boolean
  importedCount: number
  errors: string[]
  timecards: TimecardData[]
}

/**
 * Emmy Health Upload Row Result
 * Result for each row processed during Emmy Health upload
 */
export interface EmmyHealthUploadRowResult {
  row: number
  employeeUsername: string
  firstName: string
  lastName: string
  ssnLast4: string
  status: 'success' | 'failed' | 'skipped' | 'no_match'
  paystubId?: string
  wellnessAmount?: number
  claimAmount?: number
  error?: string
}

/**
 * Emmy Health Upload Result
 * Response from uploading Emmy Health CSV data for a batch
 */
export interface EmmyHealthUploadResult {
  batchId: string
  totalRows: number
  successCount: number
  failedCount: number
  skippedCount: number
  noMatchCount: number
  results: EmmyHealthUploadRowResult[]
}

// ==================== EMMY HEALTH BULK UPLOAD TYPES ====================

export interface CandidateMatch {
  batchId: string
  batchName: string
  matchedRows: number
  totalRows: number
  matchScore: number
}

export interface SplitPlanRoutedBatch {
  batchId: string
  batchName: string
  rowCount: number
  wellnessAmount?: number
  claimAmount?: number
}

export interface SplitPlanAmbiguousRow {
  rowIndex: number
  employeeDisplay: string
  candidateBatchIds: string[]
}

export interface SplitPlanUnmatchedRow {
  rowIndex: number
  employeeDisplay: string
}

/** Present when a file's rows route to 2+ batches. */
export interface SplitPlan {
  routedBatches: SplitPlanRoutedBatch[]
  unmatchedCount: number
  unmatchedRows?: SplitPlanUnmatchedRow[]
  ambiguousRows: SplitPlanAmbiguousRow[]
}

export interface GroupNameSuggestion {
  batchId: string
  groupName: string
  /** The matching batch's company name (MSP: was matchedSiteName in SHR). */
  matchedCompanyName: string
  confidence: 'name_match' | 'partial_name'
}

export interface FilePreviewEntry {
  fileName: string
  contentHash: string
  totalRows: number
  parseError?: string
  autoMatchedBatchId?: string
  matchScore?: number
  candidateMatches?: CandidateMatch[]
  /** Present when rows route to 2+ batches; single-batch files omit this. */
  splitPlan?: SplitPlan
  /** Present when zero employee-ID matches but GroupName matched exactly one eligible batch. */
  groupNameSuggestion?: GroupNameSuggestion
  totals?: {wellnessAmount: number; claimAmount: number}
}

/** Union assignment type sent to the execute endpoint. */
export type ExecuteAssignment =
  | {fileName: string; mode?: 'whole'; batchId: string}
  | {fileName: string; mode: 'split'; resolutions?: Array<{rowIndex: number; batchId: string}>}

export interface BatchPreviewEntry {
  batchId: string
  batchName: string
  batchNumber: string
  status: string
  paymentPeriodStart: string
  paymentPeriodEnd: string
  isEligible: boolean
  employeeCount: number
  /** How many paystubs already carry Emmy lines (0 = not yet applied). */
  emmyAppliedCount: number
  /** Human-readable agency/EOR name (MSP: was eorName in SHR). */
  agencyEorName: string
  /** Human-readable company/client name (MSP: was siteName in SHR). */
  companyName: string
  /** Agency ID — populated when scope filter applied (MSP: was eorId in SHR). */
  agencyId?: string
  /** Company ID — populated when scope filter applied (MSP: was siteId in SHR). */
  companyId?: string
}

export interface BulkUploadPreviewResponse {
  weekRange: {from: string; to: string}
  files: FilePreviewEntry[]
  batches: BatchPreviewEntry[]
  duplicateFiles: string[]
}

export interface SplitBatchResult {
  batchId: string
  batchName: string
  status: 'success' | 'failed'
  error?: string
  result?: EmmyHealthUploadResult
}

export interface SplitResultSummary {
  batchesProcessed: number
  batchesSucceeded: number
  batchesFailed: number
  totalRowsRouted: number
  unmatchedRows: number
  ambiguousSkippedRows: number
}

export interface BatchUploadResult {
  fileName: string
  batchId?: string
  batchName?: string
  status: 'success' | 'failed'
  error?: string
  result?: EmmyHealthUploadResult
  mode?: 'whole' | 'split'
  splitResults?: SplitBatchResult[]
  splitSummary?: SplitResultSummary
}

export interface BulkUploadExecuteResponse {
  results: BatchUploadResult[]
  summary: {
    total: number
    succeeded: number
    failed: number
  }
}

// ==================== PAY ALERT TYPES ====================

/**
 * A single employee flagged as having guaranteed-zero net pay before batch approval.
 * All monetary values are in dollars (matching the MSP/BE convention).
 * Mirrors PayAlertItemResponse from the backend DTO.
 */
export interface PayAlertItem {
  applicantId: string
  employeeName: string
  /** Estimated gross pay for this pay period (sum of invoice item pay), in dollars. */
  estimatedGross: number
  /** Federal additional withholding (from taxInfo or w4.extraWithholding), in dollars. */
  federalAdditionalWithholding: number
  /** State additional withholding (from taxInfo or caedd.additionalAmount), in dollars. */
  stateAdditionalWithholding: number
  /** Sum of unconditional flat/monthlyPremium deductions, in dollars. */
  fixedDeductions: number
  /** Sum of fixed-amount garnishments, in dollars. */
  fixedGarnishments: number
  /** Sum of active carry-forward balances from prior shortfalls, in dollars. */
  carryForward: number
  /** Total unconditional withholding = fedAddl + stateAddl + fixedDeductions + fixedGarnishments + carryForward, in dollars. */
  totalFixedWithholding: number
  /** Projected net pay — always 0 for flagged employees (withholding >= gross). */
  projectedNet: 0
}

/**
 * Full response for GET /payroll/batches/:batchId/pay-alerts.
 * Mirrors PayAlertResponse from the backend DTO.
 */
export interface PayAlertResponse {
  batchId: string
  payAlertCount: number
  /** Human-readable summary for tooltip display; empty string when no alerts. */
  payAlertSummary: string
  payAlerts: PayAlertItem[]
}

/**
 * Response from GET /payroll/invoices/eor/:eorId/next-invoice-number
 * nextInvoiceNumber is absent when no prefix has been configured.
 */
export interface EorNextInvoiceNumberResponse {
  eorId: string
  invoicePrefix?: string
  nextInvoiceNumber?: string
}

/**
 * Response from POST /payroll/invoices/eor/:eorId/invoice-counter
 */
export interface EorInvoiceCounterResponse {
  eorId: string
  lastIssued: number
  nextInvoiceNumber?: string
}

/**
 * STFFLG-4179 D3: GET /payroll/batches can send the batch's own ActorStamp `{userId, email, at}` in
 * `approvedBy` (stamped at PaystubsApproved, and left in place when the server's invoice read
 * fails). An approver is a string or nothing: the stamp's email, else unset.
 */
const approverString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value
  const email = value && typeof value === 'object' ? (value as {email?: unknown}).email : undefined
  return typeof email === 'string' ? email : undefined
}

/**
 * A batch whose `approvedBy` (or a nested revision's) is a string or unset, so the Batches cell,
 * tooltip, test id, filter and sort only ever see a string. A batch with nothing to normalize comes
 * back as the same object.
 */
export const normalizeBatchApprover = (batch: BatchResponse): BatchResponse => {
  const approvedBy: unknown = batch.approvedBy
  const approverChanged = approvedBy != null && typeof approvedBy !== 'string'
  const revisions = batch.revisions?.map(normalizeBatchApprover)
  const revisionsChanged =
    revisions?.some((revision, i) => revision !== batch.revisions?.[i]) === true
  if (!approverChanged && !revisionsChanged) return batch
  return {
    ...batch,
    ...(approverChanged ? {approvedBy: approverString(approvedBy)} : {}),
    ...(revisionsChanged ? {revisions} : {}),
  }
}

/**
 * Payroll API Service
 * Handles all payroll-related API calls
 * Extends BaseApi for automatic Bearer token authorization
 */
export class PayrollApi extends BaseApi {
  // ==================== EOR ENDPOINTS ====================

  /* UPDATED REQUIREMENT (Jan 2025): Removed getEors() endpoint
     Use agencyApi.getAllEors() instead - the payroll/eors endpoint will be removed by BE
     
     OLD CODE (preserved for reference):
     async getEors(): Promise<EORListResponse> {
       try {
         const response = await this.client.get<EORListResponse>('/payroll/eors')
         return response.data
       } catch (error: unknown) {
         reportCatchError('Error fetching EORs:', error)
         throw error
       }
     }
  */

  // ==================== CLIENT ENDPOINTS ====================

  /**
   * Get clients by EOR/Agency ID
   * GET /payroll/eors/:agencyId/clients
   *
   * @param agencyId - The agency/EOR ID
   */
  async getClientsByEOR(agencyId: string): Promise<ClientListResponse> {
    try {
      const response = await this.client.get<ClientListResponse>(
        `/payroll/eors/${agencyId}/clients`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching clients for EOR:', error)
      throw error
    }
  }

  // ==================== BATCH ENDPOINTS ====================

  /**
   * Create a new payroll batch
   * POST /payroll/batches
   *
   * @param request - Batch creation parameters
   */
  async createBatch(request: CreateBatchRequest): Promise<BatchResponse> {
    try {
      const response = await this.client.post<BatchResponse>('/payroll/batches', request)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating batch:', error)
      throw error
    }
  }

  /**
   * Start an overpayment-recovery correction batch.
   * POST /payroll/batches/correction
   *
   * Creates a non-disbursing Custom batch that mirrors the selected already-paid
   * paystubs so the admin can edit the amounts down and approve; on approval the
   * originals are reversed and an Overpayment Recovery deduction is attached.
   *
   * @param request - Correction batch parameters (original batch + paystub IDs)
   */
  async startCorrectionBatch(request: StartCorrectionBatchRequest): Promise<BatchResponse> {
    try {
      const response = await this.client.post<BatchResponse>('/payroll/batches/correction', request)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error starting correction batch:', error)
      throw error
    }
  }

  /**
   * Get batch by ID
   * GET /payroll/batches/:batchId
   *
   * @param batchId - The batch ID
   */
  async getBatchById(batchId: string): Promise<BatchResponse> {
    try {
      const response = await this.client.get<BatchResponse>(`/payroll/batches/${batchId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching batch:', error)
      throw error
    }
  }

  /**
   * Get all batches with optional filters
   * GET /payroll/batches?agencyId=xxx&companyId=xxx&status=xxx
   *
   * @param filters - Optional filter parameters
   */
  async getBatches(filters?: {
    /**
     * STFFLG-4179: also return the Batches table's figures (`totalBill`, `totalInvoice`,
     * `totalInvoiceBeforeAdjustments`, `employeeCount`, `hasLiveInvoice` and their error flags),
     * which cost the server two more reads. Only the Batches table's own list load sends it, and
     * only with the `batchTableColumns` flag on; the archived count, the batch-details dropdown and
     * the report modal never do.
     */
    includeTotals?: boolean
    agencyId?: string
    companyId?: string
    status?: BatchStatus
    archivedOnly?: boolean
    /** STFFLG-4116: the backend omits Voided batches unless asked for them (or filtered to that status). */
    includeVoided?: boolean
  }): Promise<BatchResponse[]> {
    try {
      const params = new URLSearchParams()
      if (filters?.includeTotals) params.append('includeTotals', 'true')
      if (filters?.agencyId) params.append('agencyId', filters.agencyId)
      if (filters?.companyId) params.append('companyId', filters.companyId)
      if (filters?.status) params.append('status', filters.status)
      if (filters?.archivedOnly) params.append('archivedOnly', 'true')
      if (filters?.includeVoided) params.append('includeVoided', 'true')

      const response = await this.client.get<BatchResponse[]>(
        `/payroll/batches${params.toString() ? `?${params.toString()}` : ''}`,
      )
      // STFFLG-4179 D3: every caller gets a string approver (normalizeBatchApprover).
      return Array.isArray(response.data)
        ? response.data.map(normalizeBatchApprover)
        : response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching batches:', error)
      throw error
    }
  }

  /**
   * Update an existing batch
   * PUT /payroll/batches/:batchId
   * UPDATED: Uses correct field names for mspplatform backend
   *
   * @param request - Batch update parameters
   */
  async updateBatch(request: UpdateBatchRequest): Promise<BatchResponse> {
    try {
      const {id, ...updateData} = request
      const response = await this.client.put<BatchResponse>(`/payroll/batches/${id}`, updateData)
      // BE logs a batch-edit activity entry as part of this request.
      emitBatchActivity(id)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating batch:', error)
      throw error
    }
  }

  /**
   * Delete a batch (cleanup on invoice creation failure)
   * DELETE /payroll/batches/:batchId
   * Only works if batch has no invoice attached.
   *
   * @param batchId - The batch ID to delete
   */
  async deleteBatch(batchId: string): Promise<void> {
    try {
      await this.client.delete(`/payroll/batches/${batchId}`)
      emitBatchActivity(batchId)
    } catch (error: unknown) {
      reportCatchError('Error deleting batch:', error)
      throw error
    }
  }

  /**
   * Void a payroll batch and its invoice
   * POST /payroll/batches/:batchId/void
   * Marks both the batch and its associated invoice as voided.
   * Cannot void if batch is Finalized/Approved/Invoiced/Completed
   * or if invoice is Approved/Paid.
   *
   * @param batchId - The batch ID to void
   * @param reason - Optional reason for voiding
   */
  async voidBatch(batchId: string, reason?: string): Promise<BatchResponse> {
    try {
      const response = await this.client.post<BatchResponse>(
        `/payroll/batches/${batchId}/void`,
        reason ? {reason} : {},
      )
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error voiding batch:', error)
      throw error
    }
  }

  /**
   * Archive a payroll batch
   * POST /payroll/batches/:batchId/archive
   */
  async archiveBatch(batchId: string): Promise<BatchResponse> {
    try {
      const response = await this.client.post<BatchResponse>(`/payroll/batches/${batchId}/archive`)
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error archiving batch:', error)
      throw error
    }
  }

  /**
   * Unarchive a payroll batch
   * POST /payroll/batches/:batchId/unarchive
   */
  async unarchiveBatch(batchId: string): Promise<BatchResponse> {
    try {
      const response = await this.client.post<BatchResponse>(
        `/payroll/batches/${batchId}/unarchive`,
      )
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error unarchiving batch:', error)
      throw error
    }
  }

  /**
   * Finalize a payroll batch
   * POST /payroll/batches/:batchId/finalize
   *
   * @param batchId - The batch ID to finalize
   */
  async finalizeBatch(batchId: string): Promise<BatchResponse> {
    try {
      const response = await this.client.post<BatchResponse>(`/payroll/batches/${batchId}/finalize`)
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error finalizing batch:', error)
      throw error
    }
  }

  /**
   * UPDATED REQUIREMENT (Jan 2025): Process payroll - send batch to payroll software
   * Called after customer approves invoice
   * Sends batch data to payroll provider microservice
   * Transitions batch status from "Approved" → "Closed"
   *
   * POST /payroll/batches/:batchId/process
   * TODO: Update endpoint path when backend implements this
   *
   * @param batchId - The batch ID to process
   */
  async processPayroll(batchId: string): Promise<BatchResponse> {
    try {
      const response = await this.client.post<BatchResponse>(`/payroll/batches/${batchId}/process`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error processing payroll for batch:', error)
      throw error
    }
  }

  /**
   * Send batch to payroll
   * Called after processPayroll to send the batch data to the external payroll system
   *
   * POST /payroll/batches/:batchId/send-to-payroll
   *
   * @param batchId - The batch ID to send to payroll
   */
  async sendToPayroll(batchId: string): Promise<SendToPayrollResponse> {
    try {
      const response = await this.client.post<SendToPayrollResponse>(
        `/payroll/batches/${batchId}/send-to-payroll`,
      )
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error sending batch to payroll:', error)
      throw error
    }
  }

  /**
   * KRAF-752: ask payroll to re-send its current state for a sent batch (payment
   * statuses, check numbers, exclusions). Read-only on the payroll side: it never
   * creates or changes a payment. An excluded employee comes back as approved/unsent
   * and the batch reopens as PaystubsApproved.
   * POST /payroll/batches/:batchId/sync-from-payroll
   */
  async syncFromPayroll(batchId: string): Promise<{eventsQueued: number}> {
    try {
      const response = await this.client.post<{eventsQueued: number}>(
        `/payroll/batches/${batchId}/sync-from-payroll`,
      )
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error syncing batch from payroll:', error)
      throw error
    }
  }

  // ==================== EARNINGS ENDPOINTS ====================

  /**
   * Create batch earnings records (calculate from timecards)
   * POST /payroll/earnings/batch
   *
   * @param request - Earnings creation parameters with timecards
   */
  async createBatchEarnings(
    request: CreateBatchEarningsRequest,
  ): Promise<EarningsRecordResponse[]> {
    try {
      const response = await this.client.post<EarningsRecordResponse[]>(
        '/payroll/earnings/batch',
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating batch earnings:', error)
      throw error
    }
  }

  /**
   * Get earnings records by batch ID
   * GET /payroll/earnings/batch/:batchId
   *
   * @param batchId - The batch ID
   */
  async getEarningsByBatchId(batchId: string): Promise<EarningsRecordResponse[]> {
    try {
      const response = await this.client.get<EarningsRecordResponse[]>(
        `/payroll/earnings/batch/${batchId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching earnings for batch:', error)
      throw error
    }
  }

  /**
   * Get earnings history for an employee/applicant
   * GET /payroll/earnings/applicant/:applicantId
   *
   * @param applicantId - The applicant/employee ID
   */
  async getEarningsByApplicant(applicantId: string): Promise<EarningsRecordResponse[]> {
    try {
      const response = await this.client.get<EarningsRecordResponse[]>(
        `/payroll/earnings/applicant/${applicantId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching earnings for applicant:', error)
      throw error
    }
  }

  /**
   * Get specific earnings record (paystub)
   * GET /payroll/earnings/:earningsId
   *
   * @param earningsId - The earnings record ID
   */
  async getEarningsById(earningsId: string): Promise<EarningsRecordResponse> {
    try {
      const response = await this.client.get<EarningsRecordResponse>(
        `/payroll/earnings/${earningsId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching earnings record:', error)
      throw error
    }
  }

  /**
   * Get employee YTD tax information
   * GET /payroll/earnings/ytd/:applicantId/:year
   *
   * @param applicantId - The applicant/employee ID
   * @param year - The tax year
   */
  async getYTDTaxInfo(applicantId: string, year: number): Promise<unknown> {
    try {
      const response = await this.client.get<unknown>(
        `/payroll/earnings/ytd/${applicantId}/${year}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching YTD tax info:', error)
      throw error
    }
  }

  // ==================== INVOICE ENDPOINTS ====================

  /**
   * Generate invoice from batch with unprocessed timecards
   * POST /payroll/batches/:batchId/generate-invoice
   * UPDATED: New endpoint for auto-generating invoice from batch
   *
   * @param batchId - The batch ID
   */
  async generateInvoiceFromBatch(batchId: string): Promise<GenerateInvoiceResponse> {
    try {
      const response = await this.client.post<GenerateInvoiceResponse>(
        `/payroll/batches/${batchId}/generate-invoice`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error generating invoice from batch:', error)
      throw error
    }
  }

  /**
   * Re-sync invoice items from latest timecard data
   * POST /payroll/batches/:batchId/resync-invoice
   */
  async resyncInvoice(batchId: string): Promise<GenerateInvoiceResponse> {
    try {
      const response = await this.client.post<GenerateInvoiceResponse>(
        `/payroll/batches/${batchId}/resync-invoice`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error re-syncing invoice:', error)
      throw error
    }
  }

  /**
   * Create blank invoice for manual entry
   * POST /payroll/invoices/blank
   * UPDATED: New endpoint for creating empty invoices
   *
   * @param request - Blank invoice creation parameters
   */
  async createBlankInvoice(request: CreateBlankInvoiceRequest): Promise<InvoiceResponse> {
    try {
      const response = await this.client.post<InvoiceResponse>('/payroll/invoices/blank', request)
      emitBatchActivity(response.data.batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating blank invoice:', error)
      throw error
    }
  }

  /**
   * Generate invoice from approved batch (LEGACY - kept for compatibility)
   * POST /payroll/invoices
   *
   * @param request - Invoice creation parameters
   */
  async generateInvoice(request: CreateInvoiceRequest): Promise<InvoiceWithItemsResponse> {
    try {
      const response = await this.client.post<InvoiceWithItemsResponse>(
        '/payroll/invoices',
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error generating invoice:', error)
      throw error
    }
  }

  /**
   * Get invoices with optional filters
   * GET /payroll/invoices?batchId=xxx&companyId=xxx&status=xxx&page=1&limit=20
   *
   * @param filters - Optional filter parameters
   */
  async getInvoices(filters?: {
    batchId?: string
    companyId?: string
    agencyId?: string
    status?: InvoiceStatus
    page?: number
    limit?: number
  }): Promise<{
    invoices: InvoiceResponse[]
    total: number
    page: number
    limit: number
    totalPages: number
  }> {
    try {
      const params = new URLSearchParams()
      if (filters?.batchId) params.append('batchId', filters.batchId)
      if (filters?.companyId) params.append('companyId', filters.companyId)
      if (filters?.agencyId) params.append('agencyId', filters.agencyId)
      if (filters?.status) params.append('status', filters.status)
      if (filters?.page) params.append('page', filters.page.toString())
      if (filters?.limit) params.append('limit', filters.limit.toString())

      const response = await this.client.get<{
        invoices: InvoiceResponse[]
        total: number
        page: number
        limit: number
        totalPages: number
      }>(`/payroll/invoices${params.toString() ? `?${params.toString()}` : ''}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching invoices:', error)
      throw error
    }
  }

  /**
   * Get invoice details with line items
   * GET /payroll/invoices/:invoiceId
   *
   * @param invoiceId - The invoice ID
   */
  async getInvoiceById(invoiceId: string): Promise<InvoiceWithItemsResponse> {
    try {
      const response = await this.client.get<InvoiceWithItemsResponse>(
        `/payroll/invoices/${invoiceId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching invoice:', error)
      throw error
    }
  }

  /**
   * Approve an invoice.
   * @param invoiceId - The invoice ID to approve
   * @param sendToClient true = approve AND email the client now (default); false = approve but skip
   *   emailing. The invoice can be emailed later via sendInvoiceToClient().
   * @param generatePaystubs true = generate paystubs at approval (default); false = approve WITHOUT
   *   generating paystubs — the invoice is billing-only. BE only accepts false for revision batch
   *   invoices.
   */
  async approveInvoice(
    invoiceId: string,
    sendToClient = true,
    generatePaystubs?: boolean,
  ): Promise<InvoiceResponse> {
    try {
      const response = await this.client.post<InvoiceResponse>(
        `/payroll/invoices/${invoiceId}/approve`,
        // Omit generatePaystubs unless explicitly chosen — the BE defaults to true and
        // rejects false for non-revision invoices.
        generatePaystubs === undefined ? {sendToClient} : {sendToClient, generatePaystubs},
      )
      // Approving flips the owning batch to Invoiced; BE tags that entry with the batchId.
      emitBatchActivity(response.data.batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error approving invoice:', error)
      throw error
    }
  }

  /**
   * Approve EVERY outstanding invoice of a batch — all or nothing.
   * POST /payroll/batches/:batchId/approve
   *
   * A batch split by Max Invoice Amount / Separate Invoice By is one billing decision, so it is
   * approved as one: if any invoice cannot be approved, none is (the backend rolls back any it had
   * written) and the call fails with the reason. Paystubs, the batch status and the client email
   * then follow once. Send/Skip stays per invoice.
   *
   * @param decisions per-invoice Send/Skip; an outstanding invoice not listed uses `sendToClient`.
   * @param generatePaystubs omit for the BE default (generate); false only for revision batches.
   */
  async approveBatchInvoices(
    batchId: string,
    options: {
      decisions?: Array<{invoiceId: string; sendToClient: boolean}>
      sendToClient?: boolean
      generatePaystubs?: boolean
    } = {},
  ): Promise<{invoices: InvoiceResponse[]}> {
    try {
      const response = await this.client.post<{invoices: InvoiceResponse[]}>(
        `/payroll/batches/${batchId}/approve`,
        {
          ...(options.decisions ? {invoices: options.decisions} : {}),
          ...(options.sendToClient === undefined ? {} : {sendToClient: options.sendToClient}),
          ...(options.generatePaystubs === undefined
            ? {}
            : {generatePaystubs: options.generatePaystubs}),
        },
      )
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error approving batch invoices:', error)
      throw error
    }
  }

  /**
   * Does the batch need a re-split, and what would one do (reasons + preview).
   * GET /payroll/batches/:batchId/split-status
   */
  async getBatchSplitStatus(batchId: string): Promise<BatchSplitStatus> {
    try {
      const response = await this.client.get<BatchSplitStatus>(
        `/payroll/batches/${batchId}/split-status`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error loading batch split status:', error)
      throw error
    }
  }

  /**
   * Apply the re-split previewed by getBatchSplitStatus. Returns the moves made and the batch's
   * state afterwards (`applied: false` when nothing needed re-splitting any more).
   * POST /payroll/batches/:batchId/resplit
   */
  async resplitBatch(batchId: string): Promise<BatchSplitStatus & {applied: boolean}> {
    try {
      const response = await this.client.post<BatchSplitStatus & {applied: boolean}>(
        `/payroll/batches/${batchId}/resplit`,
      )
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error re-splitting batch invoices:', error)
      throw error
    }
  }

  /**
   * Send an already-approved-but-skipped invoice to the client.
   * POST /payroll/invoices/:invoiceId/send-to-client
   * Only valid when the invoice is Approved and clientDeliveryStatus === 'Skipped'.
   * Can be called repeatedly; each call appends a clientDeliveries entry.
   * - Omitting body.recipients → sends to all billing deliveryEmails.
   * - recipients must be a non-empty subset of the client's billing deliveryEmails, else 400.
   * - Empty deliveryEmails list → 400 (no recipients configured).
   *
   * @param invoiceId - The invoice ID
   * @param body      - Optional: restrict delivery to a subset of billing emails
   */
  async sendInvoiceToClient(
    invoiceId: string,
    body?: {recipients?: string[]},
  ): Promise<InvoiceResponse> {
    try {
      const response = await this.client.post<InvoiceResponse>(
        `/payroll/invoices/${invoiceId}/send-to-client`,
        body ?? {},
      )
      emitBatchActivity(response.data.batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error sending invoice to client:', error)
      throw error
    }
  }

  /**
   * Send invoice to customer
   * POST /payroll/invoices/:invoiceId/send
   * Updates invoice status to Sent with timestamp and user who sent it.
   * Only works on Pending invoices.
   *
   * @param invoiceId - The invoice ID to send
   */
  async sendInvoice(invoiceId: string): Promise<InvoiceResponse> {
    try {
      const response = await this.client.post<InvoiceResponse>(
        `/payroll/invoices/${invoiceId}/send`,
      )
      emitBatchActivity(response.data.batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error sending invoice:', error)
      throw error
    }
  }

  /**
   * Unsend invoice - revert to Pending
   * POST /payroll/invoices/:invoiceId/unsend
   * Reverts invoice status from Sent back to Pending.
   * Clears sent timestamp and user info.
   *
   * @param invoiceId - The invoice ID to unsend
   */
  async unsendInvoice(invoiceId: string): Promise<InvoiceResponse> {
    try {
      const response = await this.client.post<InvoiceResponse>(
        `/payroll/invoices/${invoiceId}/unsend`,
      )
      emitBatchActivity(response.data.batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error unsending invoice:', error)
      throw error
    }
  }

  /**
   * Download invoice PDF
   * GET /payroll/invoices/:invoiceId/pdf
   *
   * @param invoiceId - The invoice ID
   * @returns PDF blob
   */
  async downloadInvoicePDF(invoiceId: string): Promise<Blob> {
    try {
      const response = await this.client.get(`/payroll/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error downloading invoice PDF:', error)
      throw error
    }
  }

  /**
   * Download batch report as Excel (XLSX)
   * GET /payroll/invoices/:invoiceId/batch-report-xlsx
   */
  async downloadBatchReportXlsx(invoiceId: string): Promise<Blob> {
    try {
      const response = await this.client.get(`/payroll/invoices/${invoiceId}/batch-report-xlsx`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error downloading batch report XLSX:', error)
      throw error
    }
  }

  // ==================== INVOICE ITEM CRUD ENDPOINTS ====================

  /**
   * Add invoice item (employee) to invoice
   * POST /payroll/invoices/:invoiceId/items
   * UPDATED: New endpoint for manually adding employees to invoices
   *
   * @param invoiceId - The invoice ID
   * @param request - Invoice item creation parameters
   */
  async addInvoiceItem(invoiceId: string, request: AddInvoiceItemRequest): Promise<InvoiceItem> {
    try {
      const response = await this.client.post<InvoiceItem>(
        `/payroll/invoices/${invoiceId}/items`,
        request,
      )
      // Adding an employee (invoice item) writes a batch activity entry.
      emitBatchActivity(response.data.batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error adding invoice item:', error)
      throw error
    }
  }

  /**
   * Update invoice item
   * PUT /payroll/invoices/:invoiceId/items/:itemId
   * UPDATED: New endpoint for updating employee earnings in invoice
   *
   * @param invoiceId - The invoice ID
   * @param itemId - The invoice item ID
   * @param request - Invoice item update parameters
   */
  async updateInvoiceItem(
    invoiceId: string,
    itemId: string,
    request: UpdateInvoiceItemRequest,
  ): Promise<InvoiceItem> {
    try {
      const response = await this.client.put<InvoiceItem>(
        `/payroll/invoices/${invoiceId}/items/${itemId}`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating invoice item:', error)
      throw error
    }
  }

  /**
   * Delete invoice item (remove employee from invoice)
   * DELETE /payroll/invoices/:invoiceId/items/:itemId
   * UPDATED: New endpoint for removing employees from invoices
   *
   * @param invoiceId - The invoice ID
   * @param itemId - The invoice item ID
   */
  async deleteInvoiceItem(invoiceId: string, itemId: string): Promise<void> {
    try {
      await this.client.delete(`/payroll/invoices/${invoiceId}/items/${itemId}`)
    } catch (error: unknown) {
      reportCatchError('Error deleting invoice item:', error)
      throw error
    }
  }

  // ==================== EARNINGS LINE ENDPOINTS ====================

  /**
   * Add a new earnings line to an existing invoice item
   * POST /payroll/invoices/:invoiceId/items/:itemId/earnings
   *
   * @param invoiceId - The invoice ID
   * @param itemId - The invoice item ID
   * @param request - Earnings line data to add
   */
  async addEarningsLine(
    invoiceId: string,
    itemId: string,
    request: EarningsLine,
  ): Promise<InvoiceItem> {
    try {
      const response = await this.client.post<InvoiceItem>(
        `/payroll/invoices/${invoiceId}/items/${itemId}/earnings`,
        request,
      )
      // Adding an earnings-line row writes a batch activity entry.
      emitBatchActivity(response.data.batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error adding earnings line:', error)
      throw error
    }
  }

  /**
   * Update specific earnings line within an invoice item
   * PUT /payroll/invoices/:invoiceId/items/:itemId/earnings/:paycodeId
   * ADDED: New endpoint for updating individual earnings lines
   *
   * @param invoiceId - The invoice ID
   * @param itemId - The invoice item ID
   * @param paycodeId - The paycode ID of the earnings line to update
   * @param request - Partial earnings line update parameters (only provided fields are updated)
   */
  async updateEarningsLine(
    invoiceId: string,
    itemId: string,
    paycodeId: string,
    request: UpdateEarningsLineRequest,
  ): Promise<InvoiceItem> {
    try {
      const response = await this.client.put<InvoiceItem>(
        `/payroll/invoices/${invoiceId}/items/${itemId}/earnings/${paycodeId}`,
        request,
      )
      // Editing an earnings-line (a line item) writes a batch activity entry.
      emitBatchActivity(response.data.batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating earnings line:', error)
      throw error
    }
  }

  /**
   * Delete specific earnings line from an invoice item
   * DELETE /payroll/invoices/:invoiceId/items/:itemId/earnings/:paycodeId
   * ADDED: New endpoint for removing individual earnings lines
   * Note: If no earnings lines remain after deletion, the entire invoice item is deleted
   *
   * @param invoiceId - The invoice ID
   * @param itemId - The invoice item ID
   * @param paycodeId - The paycode ID of the earnings line to delete (legacy fallback identifier)
   * @param lineId - Stable line id; required to disambiguate when multiple lines share the same paycodeId
   */
  async deleteEarningsLine(
    invoiceId: string,
    itemId: string,
    paycodeId: string,
    lineId?: string,
  ): Promise<void> {
    try {
      await this.client.delete(
        `/payroll/invoices/${invoiceId}/items/${itemId}/earnings/${paycodeId}`,
        lineId ? {params: {lineId}} : undefined,
      )
    } catch (error: unknown) {
      reportCatchError('Error deleting earnings line:', error)
      throw error
    }
  }

  // ==================== TIMECARD ENDPOINTS ====================

  /**
   * Receive timecard data from Ronos webhook (Single or Batch)
   * POST /webhooks/ronos/timecards
   *
   * @param request - Ronos timecard data
   */
  async receiveRonosTimecards(request: unknown): Promise<unknown> {
    try {
      const response = await this.client.post<unknown>('/webhooks/ronos/timecards', request)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error receiving Ronos timecards:', error)
      throw error
    }
  }

  /**
   * Get timecards with optional filters
   * GET /payroll/timecards
   *
   * @param filters - Optional filter parameters
   */
  async getTimecards(filters?: {
    batchId?: string
    applicantId?: string
    assignmentId?: string
    weekStartDate?: string
    weekEndDate?: string
    startDate?: string
    endDate?: string
    processed?: boolean
  }): Promise<TimecardResponse[]> {
    try {
      const params = new URLSearchParams()
      if (filters?.batchId) params.append('batchId', filters.batchId)
      if (filters?.applicantId) params.append('applicantId', filters.applicantId)
      if (filters?.assignmentId) params.append('assignmentId', filters.assignmentId)
      if (filters?.weekStartDate) params.append('weekStartDate', filters.weekStartDate)
      if (filters?.weekEndDate) params.append('weekEndDate', filters.weekEndDate)
      if (filters?.startDate) params.append('startDate', filters.startDate)
      if (filters?.endDate) params.append('endDate', filters.endDate)
      if (filters?.processed !== undefined) params.append('processed', String(filters.processed))

      const response = await this.client.get<TimecardResponse[]>(
        `/payroll/timecards${params.toString() ? `?${params.toString()}` : ''}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching timecards:', error)
      throw error
    }
  }

  /**
   * Get timecards by assignment ID
   * GET /payroll/timecards/assignment/:assignmentId
   *
   * @param assignmentId - The assignment ID
   */
  async getTimecardsByAssignment(assignmentId: string): Promise<TimecardResponse[]> {
    try {
      const response = await this.client.get<TimecardResponse[]>(
        `/payroll/timecards/assignment/${assignmentId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching timecards for assignment:', error)
      throw error
    }
  }

  /**
   * Get all unprocessed timecards for batch creation
   * GET /payroll/timecards/unprocessed
   */
  async getUnprocessedTimecards(): Promise<TimecardData[]> {
    try {
      const response = await this.client.get<TimecardData[]>('/payroll/timecards/unprocessed')
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching unprocessed timecards:', error)
      throw error
    }
  }

  /**
   * Get paginated timecards with filters and sorting
   * GET /payroll/timecards/paginated
   *
   * @param params - Pagination, filter, and sort parameters
   * @returns Paginated timecard response with employee, site, department, and job title details
   */
  async getPaginatedTimecards(params: TimecardPagingParams): Promise<TimecardPagingResponse> {
    try {
      const queryParams = new URLSearchParams()
      queryParams.append('pageNumber', params.pageNumber.toString())
      queryParams.append('pageSize', params.pageSize.toString())

      if (params.companyId) queryParams.append('companyId', params.companyId)
      if (params.siteId) queryParams.append('siteId', params.siteId)
      if (params.departmentId) queryParams.append('departmentId', params.departmentId)
      if (params.jobTitle) queryParams.append('jobTitle', params.jobTitle)
      if (params.employeeName) queryParams.append('employeeName', params.employeeName)
      if (params.weekStartDate) queryParams.append('weekStartDate', params.weekStartDate)
      if (params.weekEndDate) queryParams.append('weekEndDate', params.weekEndDate)
      if (params.sortBy) queryParams.append('sortBy', params.sortBy)
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)

      const response = await this.client.get<TimecardPagingResponse>(
        `/payroll/timecards/paginated?${queryParams.toString()}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching paginated timecards:', error)
      throw error
    }
  }

  /**
   * Import timecard data from Toast POS system
   * POST /webhooks/toast/timecards
   *
   * @param request - Toast import parameters
   */
  async importToastTimecards(request: ToastImportRequest): Promise<ToastImportResponse> {
    try {
      const response = await this.client.post<ToastImportResponse>(
        '/webhooks/toast/timecards',
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error importing Toast timecards:', error)
      throw error
    }
  }

  /**
   * Upload CSV file for timecard import
   * POST /webhooks/toast/timecards (with FormData)
   *
   * @param file - CSV file containing timecard data
   * @param clientId - The client/company ID
   * @param eorId - The EOR/agency ID
   */
  async uploadTimecardCSV(
    file: File,
    clientId: string,
    eorId: string,
  ): Promise<ToastImportResponse> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('clientId', clientId)
      formData.append('eorId', eorId)

      const response = await this.client.post<ToastImportResponse>(
        '/webhooks/toast/timecards',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error uploading timecard CSV:', error)
      throw error
    }
  }

  // ==================== BATCH REPORT ENDPOINTS ====================

  /**
   * Generate batch report
   * POST /payroll/batches/:batchId/reports/generate
   *
   * @param batchId - The batch ID
   */
  async generateBatchReport(batchId: string): Promise<unknown> {
    try {
      const response = await this.client.post<unknown>(
        `/payroll/batches/${batchId}/reports/generate`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error generating batch report:', error)
      throw error
    }
  }

  /**
   * Get batch report metadata
   * GET /payroll/batches/:batchId/reports
   *
   * @param batchId - The batch ID
   */
  async getBatchReportMetadata(batchId: string): Promise<unknown> {
    try {
      const response = await this.client.get<unknown>(`/payroll/batches/${batchId}/reports`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching batch report metadata:', error)
      throw error
    }
  }

  /**
   * Download batch report
   * GET /payroll/batches/:batchId/reports/download
   *
   * @param batchId - The batch ID
   * @returns Report file blob
   */
  async downloadBatchReport(batchId: string): Promise<Blob> {
    try {
      const response = await this.client.get(`/payroll/batches/${batchId}/reports/download`, {
        responseType: 'blob',
      })
      // BE records a batch file-download activity entry as part of this request.
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error downloading batch report:', error)
      throw error
    }
  }

  /**
   * Download Emmy Health Report for a batch
   * GET /payroll/reports/emmy-health/batch/:batchId
   *
   * Generates and downloads an Emmy Health CSV report containing employee
   * demographics, tax withholding information, and wage data for all paystubs
   * in the specified batch.
   *
   * @param batchId - The batch ID
   * @returns CSV file blob
   */
  async downloadEmmyHealthReport(batchId: string): Promise<Blob> {
    try {
      const response = await this.client.get(`/payroll/reports/emmy-health/batch/${batchId}`, {
        responseType: 'blob',
      })
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error downloading Emmy Health report:', error)
      throw error
    }
  }

  /**
   * Download Paycheck Sign-Off Log PDF for a batch
   * GET /payroll/batches/:batchId/reports/paycheck-signoff-pdf
   *
   * Generates a printable PDF sign-off log for physical check distribution.
   * Employees sign upon receiving their paycheck.
   *
   * @param batchId - The batch ID
   * @returns PDF blob
   */
  async downloadPaycheckSignoffPdf(batchId: string): Promise<Blob> {
    try {
      const response = await this.client.get(
        `/payroll/batches/${batchId}/reports/paycheck-signoff-pdf`,
        {
          responseType: 'blob',
        },
      )
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error downloading Paycheck Sign-Off PDF:', error)
      throw error
    }
  }

  /**
   * Upload Emmy Health Report for a batch
   * POST /payroll/reports/emmy-health/batch/:batchId/upload
   *
   * Uploads an Emmy Health CSV containing per-pay-period premium and claim amounts.
   * The backend will:
   * - Match employees by EmployeeUsername or SSN last 4 digits
   * - Apply Wellness (pre-tax deduction) amounts
   * - Apply Claim (post-tax surplus to NET pay) amounts
   * - Recalculate taxes for affected paystubs
   *
   * NOTE: Upload is only allowed when batch status is "Invoiced".
   *
   * @param batchId - The batch ID
   * @param file - CSV file from Emmy Health
   * @returns Upload result with success/failure counts
   */
  async uploadEmmyHealthReport(batchId: string, file: File): Promise<EmmyHealthUploadResult> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await this.client.post<EmmyHealthUploadResult>(
        `/payroll/reports/emmy-health/batch/${batchId}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      // BE records a batch file-upload activity entry as part of this request.
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error uploading Emmy Health report:', error)
      throw error
    }
  }

  // ==================== EMMY HEALTH BULK UPLOAD ENDPOINTS ====================

  /**
   * Preview Emmy Health bulk CSV upload.
   * Dry-run: auto-routes each file's rows to Invoiced batches by employee overlap.
   * POST /payroll/reports/emmy-health/bulk-upload/preview
   *
   * @param files  - Up to 50 Emmy Health CSV files
   * @param opts   - Optional scope + date filters
   */
  async previewBulkEmmyUpload(
    files: File[],
    opts?: {
      fromDate?: string
      toDate?: string
      agencyIds?: string[]
      companyIds?: string[]
    },
  ): Promise<BulkUploadPreviewResponse> {
    try {
      const formData = new FormData()
      files.forEach((f) => formData.append('files', f))
      if (opts?.fromDate) formData.append('fromDate', opts.fromDate)
      if (opts?.toDate) formData.append('toDate', opts.toDate)
      if (opts?.agencyIds && opts.agencyIds.length > 0)
        formData.append('agencyIds', JSON.stringify(opts.agencyIds))
      if (opts?.companyIds && opts.companyIds.length > 0)
        formData.append('companyIds', JSON.stringify(opts.companyIds))
      const response = await this.client.post<BulkUploadPreviewResponse>(
        '/payroll/reports/emmy-health/bulk-upload/preview',
        formData,
        {headers: {'Content-Type': 'multipart/form-data'}},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error previewing Emmy bulk upload:', error)
      throw error
    }
  }

  /**
   * Execute Emmy Health bulk CSV upload.
   * Applies routed rows to each target batch and recalculates paystub taxes.
   * POST /payroll/reports/emmy-health/bulk-upload/execute
   *
   * @param files       - Same files sent to preview
   * @param assignments - Routing assignments (mode:'split' for auto-routed files)
   * @param opts        - Same scope + date filters used during preview
   */
  async executeBulkEmmyUpload(
    files: File[],
    assignments: ExecuteAssignment[],
    opts?: {
      fromDate?: string
      toDate?: string
      agencyIds?: string[]
      companyIds?: string[]
    },
  ): Promise<BulkUploadExecuteResponse> {
    try {
      const formData = new FormData()
      files.forEach((f) => formData.append('files', f))
      formData.append('assignments', JSON.stringify(assignments))
      if (opts?.fromDate) formData.append('fromDate', opts.fromDate)
      if (opts?.toDate) formData.append('toDate', opts.toDate)
      if (opts?.agencyIds && opts.agencyIds.length > 0)
        formData.append('agencyIds', JSON.stringify(opts.agencyIds))
      if (opts?.companyIds && opts.companyIds.length > 0)
        formData.append('companyIds', JSON.stringify(opts.companyIds))
      const response = await this.client.post<BulkUploadExecuteResponse>(
        '/payroll/reports/emmy-health/bulk-upload/execute',
        formData,
        {headers: {'Content-Type': 'multipart/form-data'}},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error executing Emmy bulk upload:', error)
      throw error
    }
  }

  // ==================== CREDIT ADJUSTMENT ENDPOINTS ====================

  /**
   * Apply a credit adjustment to an invoice
   * POST /payroll/invoices/:invoiceId/credit-adjustments
   */
  async applyCredit(
    invoiceId: string,
    request: ApplyCreditRequest,
  ): Promise<InvoiceCreditAdjustment> {
    try {
      const response = await this.client.post<InvoiceCreditAdjustment>(
        `/payroll/invoices/${invoiceId}/credit-adjustments`,
        request,
      )
      emitBatchActivity(response.data.batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error applying credit adjustment:', error)
      throw error
    }
  }

  /**
   * Get all credit adjustments for an invoice
   * GET /payroll/invoices/:invoiceId/credit-adjustments
   */
  async getCreditAdjustments(invoiceId: string): Promise<InvoiceCreditAdjustment[]> {
    try {
      const response = await this.client.get<InvoiceCreditAdjustment[]>(
        `/payroll/invoices/${invoiceId}/credit-adjustments`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching credit adjustments:', error)
      throw error
    }
  }

  /**
   * Reverse a credit adjustment
   * POST /payroll/credit-adjustments/:adjustmentId/reverse
   */
  async reverseCreditAdjustment(adjustmentId: string): Promise<InvoiceCreditAdjustment> {
    try {
      const response = await this.client.post<InvoiceCreditAdjustment>(
        `/payroll/credit-adjustments/${adjustmentId}/reverse`,
      )
      emitBatchActivity(response.data.batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error reversing credit adjustment:', error)
      throw error
    }
  }

  /**
   * Download credit memo PDF for a single credit adjustment
   * GET /payroll/credit-adjustments/:adjustmentId/credit-memo
   *
   * @param adjustmentId - The credit adjustment ID
   * @returns PDF blob (Content-Disposition: attachment; filename="credit-memo-{adjustmentNumber}.pdf")
   */
  async downloadCreditMemoPdf(adjustmentId: string): Promise<Blob> {
    try {
      const res = await this.client.get(`/payroll/credit-adjustments/${adjustmentId}/credit-memo`, {
        responseType: 'blob',
      })
      return res.data
    } catch (error: unknown) {
      reportCatchError('Error downloading credit memo PDF:', error)
      throw error
    }
  }

  // ==================== EOR INVOICE COUNTER ENDPOINTS ====================

  /**
   * Preview the next invoice number for an EOR agency.
   * GET /payroll/invoices/eor/:eorId/next-invoice-number
   *
   * Returns undefined for nextInvoiceNumber when no prefix has been set yet.
   */
  async getEorNextInvoiceNumber(eorId: string): Promise<EorNextInvoiceNumberResponse> {
    try {
      const response = await this.client.get<EorNextInvoiceNumberResponse>(
        `/payroll/invoices/eor/${eorId}/next-invoice-number`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching EOR next invoice number:', error)
      throw error
    }
  }

  /**
   * Seed or correct the EOR invoice counter (platform admin only).
   * POST /payroll/invoices/eor/:eorId/invoice-counter
   * body { lastIssued: number }
   *
   * Use this as a migration/seed tool when switching from a legacy numbering scheme.
   */
  async setEorInvoiceCounter(
    eorId: string,
    lastIssued: number,
  ): Promise<EorInvoiceCounterResponse> {
    try {
      const response = await this.client.post<EorInvoiceCounterResponse>(
        `/payroll/invoices/eor/${eorId}/invoice-counter`,
        {lastIssued},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error setting EOR invoice counter:', error)
      throw error
    }
  }

  // ==================== BATCH REVISION ENDPOINTS ====================

  /**
   * Create a revision batch from an existing batch
   * POST /payroll/batches/:batchId/create-revision
   */
  async createBatchRevision(
    batchId: string,
    request: CreateRevisionRequest,
  ): Promise<CreateRevisionResponse> {
    try {
      const response = await this.client.post<CreateRevisionResponse>(
        `/payroll/batches/${batchId}/create-revision`,
        request,
      )
      emitBatchActivity(batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating batch revision:', error)
      throw error
    }
  }

  /**
   * Update invoice details — invoice number and/or invoice date.
   * PUT /payroll/invoices/:invoiceId/details
   * Platform admin only (403 otherwise). Returns 400 if invoiceNumber is already in use.
   * Changes are audit-logged server-side.
   *
   * @param invoiceId - The invoice ID
   * @param details - Fields to update (invoiceNumber and/or invoiceDate as ISO string)
   */
  async updateInvoiceDetails(
    invoiceId: string,
    details: {invoiceNumber?: string; invoiceDate?: string},
  ): Promise<InvoiceResponse> {
    try {
      const response = await this.client.put<InvoiceResponse>(
        `/payroll/invoices/${invoiceId}/details`,
        details,
      )
      emitBatchActivity(response.data.batchId)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating invoice details:', error)
      throw error
    }
  }

  /**
   * Get all revisions for a batch
   * GET /payroll/batches/:batchId/revisions
   */
  async getBatchRevisions(batchId: string): Promise<BatchResponse[]> {
    try {
      const response = await this.client.get<BatchResponse[]>(
        `/payroll/batches/${batchId}/revisions`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching batch revisions:', error)
      throw error
    }
  }

  // ==================== PAY ALERT ENDPOINTS ====================

  /**
   * Get pre-approval pay alerts for a batch.
   * Only meaningful for pre-approval statuses (Draft/InProgress/Finalized).
   * Returns employees whose guaranteed fixed withholding >= estimated gross,
   * meaning they will net $0 if approved without correction.
   *
   * GET /payroll/batches/:batchId/pay-alerts
   *
   * @param batchId - The batch ID
   */
  async getBatchPayAlerts(batchId: string): Promise<PayAlertResponse> {
    try {
      const response = await this.client.get<PayAlertResponse>(
        `/payroll/batches/${batchId}/pay-alerts`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching pay alerts for batch:', error)
      throw error
    }
  }
}

// Export a singleton instance
export const payrollApi = new PayrollApi()

export default payrollApi
